import type { OrganizerKind } from '@pulsify/shared'
import { EVENT_TRANSLATION_CREDIT_COST } from '@pulsify/shared/dto'
import { Link } from '@tanstack/react-router'
import { Camera, ImagePlus, MapPin, Sparkles } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import {
  useOrganizerProfile,
  useSaveOrganizerProfile,
  useUploadOrganizerPicture,
} from '~/entities/channel/model/use-organizer'
import { setActiveProfileId, useOrganizationProfiles } from '~/entities/profile/model/use-profiles'
import { useTranslateDescription } from '~/features/ai-studio/hooks'
import {
  OrganizerProfileOverview,
  OrganizerProfileOverviewSkeleton,
} from '~/features/organizer/components/organizer-profile-overview'
import {
  HANDLE_MAX,
  handleFrom,
  isValidHandle,
  randomSuffix,
  textOrNull,
} from '~/features/organizer/lib/organizer-profile'
import { organizerPageHost } from '~/features/organizer/lib/public-page'
import { CreditCostTag } from '~/features/shared/components/credit-cost'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { richTextToPlainText, toRichTextHtml } from '~/shared/lib/rich-text'
import { NetworkError } from '~/shared/lib/rpc'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { PulsifyLoader } from '~/shared/ui/pulsify-loader'
import { Skeleton } from '~/shared/ui/skeleton'
import { Textarea } from '~/shared/ui/textarea'
import { PageHeader } from '~/widgets/page-header'

type Draft = {
  name: string
  handle: string
  kind: OrganizerKind
  description: string
  descriptionEn: string
  website: string
  instagram: string
}

type Slot = 'avatar' | 'cover'

const emptyDraft: Draft = {
  name: '',
  handle: '',
  kind: 'organizer',
  description: '',
  descriptionEn: '',
  website: '',
  instagram: '',
}

function useObjectUrls(files: Partial<Record<Slot, File>>): Partial<Record<Slot, string>> {
  const [urls, setUrls] = useState<Partial<Record<Slot, string>>>({})
  useEffect(() => {
    const next: Partial<Record<Slot, string>> = {}
    for (const slot of ['avatar', 'cover'] as const) {
      const file = files[slot]
      if (file) next[slot] = URL.createObjectURL(file)
    }
    setUrls(next)
    return () => {
      for (const url of Object.values(next)) URL.revokeObjectURL(url)
    }
  }, [files])
  return urls
}

/**
 * An organizer page of the business. With profiles on it shows the organizer page active in the
 * sidebar, or starts a new one (`creating`); without a page yet it is the form that creates one.
 */
export function OrganizerProfileEditor({
  creating = false,
  onCreated,
}: {
  creating?: boolean
  /** Takes over once a new page is saved, e.g. to open its own dashboard. */
  onCreated?: (profileId: string) => void
}) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? null
  const organization = useOrganizationProfiles(businessId)
  const activeOrganizer =
    organization.enabled && organization.active?.kind !== 'place'
      ? organization.active?.id
      : undefined
  const profile = useOrganizerProfile(businessId, activeOrganizer, { enabled: !creating })
  const saveProfile = useSaveOrganizerProfile(businessId)
  const upload = useUploadOrganizerPicture(businessId)
  const translate = useTranslateDescription(businessId ?? '')
  const channel = creating ? null : (profile.data ?? null)
  const [draft, setDraft] = useState<Draft>(emptyDraft)
  const [handleEdited, setHandleEdited] = useState(false)
  const [takenHandle, setTakenHandle] = useState<string | null>(null)
  const [pendingPictures, setPendingPictures] = useState<Partial<Record<Slot, File>>>({})
  const previews = useObjectUrls(pendingPictures)
  const handleId = useId()

  const handle = handleEdited ? draft.handle : handleFrom(draft.name)
  const handleError = isValidHandle(handle)
    ? takenHandle === handle
      ? m.organizerHandleTaken()
      : null
    : m.organizerHandleInvalid()

  const uploadPending = async (id: string) => {
    for (const slot of ['avatar', 'cover'] as const) {
      const file = pendingPictures[slot]
      if (!file) continue
      try {
        await upload.mutateAsync({ id, slot, file })
      } catch {
        toast.error(m.organizerPictureFailed())
      }
    }
    setPendingPictures({})
  }

  const save = (published: boolean, createHandle = handle) => {
    const links = {
      description: textOrNull(draft.description),
      descriptionEn: textOrNull(draft.descriptionEn),
      website: textOrNull(draft.website),
      instagram: textOrNull(draft.instagram),
      kind: draft.kind,
    }
    saveProfile.mutate(
      {
        create: {
          businessId: businessId ?? '',
          handle: createHandle,
          name: draft.name.trim(),
          published,
          category: draft.kind === 'venue' ? 'business' : 'community',
          ...links,
          description: links.description ?? undefined,
          descriptionEn: links.descriptionEn ?? undefined,
        },
      },
      {
        onSuccess: async (saved) => {
          toast.success(m.organizerSaved())
          if (!businessId) return
          await uploadPending(saved.id)
          if (onCreated) onCreated(saved.id)
          else setActiveProfileId(businessId, saved.id)
        },
        onError: (error) => {
          const taken = error instanceof NetworkError && error.code === 'ChannelHandleTaken'
          if (taken && !handleEdited) {
            return save(published, handleFrom(draft.name, randomSuffix()))
          }
          if (taken) {
            setTakenHandle(createHandle)
            toast.error(m.organizerHandleTaken())
            return
          }
          toast.error(m.organizerSaveFailed())
        },
      },
    )
  }
  const choosePicture = (slot: Slot, file: File) =>
    setPendingPictures((current) => ({ ...current, [slot]: file }))

  if (!businessId || (!creating && profile.isLoading)) {
    return creating ? (
      <Skeleton className="h-96 w-full max-w-2xl rounded-xl" />
    ) : (
      <OrganizerProfileOverviewSkeleton />
    )
  }

  if (channel) {
    return <OrganizerProfileOverview key={channel.id} businessId={businessId} channel={channel} />
  }

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))
  const ready = draft.name.trim().length > 0 && !handleError
  const busy = saveProfile.isPending || upload.isPending

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader title={m.organizerPageTitle()} description={m.organizerPageDescription()} />

      <Card className="overflow-hidden p-0">
        <PictureButton
          label={m.organizerCover()}
          onFile={(file) => choosePicture('cover', file)}
          className="block h-36 w-full bg-muted"
        >
          {previews.cover ? (
            <img src={previews.cover} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full items-center justify-center gap-2 text-muted-foreground text-sm">
              <ImagePlus className="h-4 w-4" />
              {m.organizerCover()}
            </span>
          )}
        </PictureButton>
        <div className="-mt-10 flex items-end gap-4 px-6 pb-5">
          <PictureButton
            label={m.organizerAvatar()}
            onFile={(file) => choosePicture('avatar', file)}
            className="h-20 w-20 shrink-0 overflow-hidden rounded-full border-4 border-card bg-muted"
          >
            {previews.avatar ? (
              <img src={previews.avatar} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="flex h-full items-center justify-center text-muted-foreground">
                <Camera className="h-5 w-5" />
              </span>
            )}
          </PictureButton>
          <div className="min-w-0 pb-1">
            <p className="truncate font-semibold">{draft.name}</p>
            <p className="truncate text-muted-foreground text-sm">@{handle}</p>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{m.organizerCreateTitle()}</CardTitle>
          <CardDescription>{m.organizerDetailsDescription()}</CardDescription>
          <p className="flex items-center gap-1.5 text-muted-foreground text-sm">
            <MapPin className="h-4 w-4 shrink-0" />
            <span>
              {m.organizerPlaceHint()}{' '}
              <Link
                to="/setup/profile"
                search={{ kind: 'place' }}
                className="font-medium text-foreground underline"
              >
                {m.organizerPlaceLink()}
              </Link>
            </span>
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label={m.organizerName()}>
            <Input
              value={draft.name}
              maxLength={80}
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>
          <div className="space-y-1.5">
            <Label htmlFor={handleId}>{m.organizerHandle()}</Label>
            <Input
              id={handleId}
              value={handle}
              maxLength={HANDLE_MAX}
              autoCapitalize="none"
              spellCheck={false}
              aria-invalid={handleError ? true : undefined}
              aria-describedby={`${handleId}-hint`}
              onChange={(e) => {
                setHandleEdited(true)
                set({ handle: e.target.value.toLowerCase() })
              }}
            />
            <p id={`${handleId}-hint`} className="text-muted-foreground text-xs">
              {organizerPageHost(handle)}
            </p>
            {handleError && handleEdited ? (
              <p className="text-destructive text-xs">{handleError}</p>
            ) : null}
          </div>
          <Field label={m.organizerAbout()}>
            <Textarea
              value={draft.description}
              maxLength={500}
              rows={4}
              onChange={(e) => set({ description: e.target.value })}
            />
          </Field>
          <div className="space-y-1.5">
            <div className="flex min-h-8 items-center justify-between gap-2">
              <Label>{m.organizerAboutEn()}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={translate.isPending}
                onClick={() => {
                  if (!draft.description.trim()) {
                    toast.error(m.eventDescriptionTranslateNeedsText())
                    return
                  }
                  translate.mutate(
                    { html: toRichTextHtml(draft.description.trim()), from: 'de', to: 'en' },
                    {
                      onSuccess: ({ html }) =>
                        set({ descriptionEn: richTextToPlainText(html).slice(0, 500) }),
                      onError: (error) => toast.error(getErrorMessageFromError(error)),
                    },
                  )
                }}
              >
                {translate.isPending ? (
                  <PulsifyLoader className="size-4" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {translate.isPending ? m.eventDescriptionTranslating() : m.organizerAboutEnAi()}
                <CreditCostTag cost={EVENT_TRANSLATION_CREDIT_COST} />
              </Button>
            </div>
            <Textarea
              value={draft.descriptionEn}
              maxLength={500}
              rows={4}
              lang="en"
              onChange={(e) => set({ descriptionEn: e.target.value })}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={m.organizerWebsite()}>
              <Input
                value={draft.website}
                placeholder="https://"
                onChange={(e) => set({ website: e.target.value })}
              />
            </Field>
            <Field label={m.instagram()}>
              <Input
                value={draft.instagram}
                placeholder="@"
                onChange={(e) => set({ instagram: e.target.value })}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button disabled={!ready || busy} onClick={() => save(true)}>
              {m.publish()}
            </Button>
            <Button variant="outline" disabled={!ready || busy} onClick={() => save(false)}>
              {m.saveAsDraft()}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
    </div>
  )
}

function PictureButton({
  label,
  onFile,
  className,
  children,
}: {
  label: string
  onFile: (file: File) => void
  className: string
  children: ReactNode
}) {
  const input = useRef<HTMLInputElement>(null)
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onFile(file)
          e.target.value = ''
        }}
      />
      <button
        type="button"
        aria-label={label}
        onClick={() => input.current?.click()}
        className={`${className} transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
      >
        {children}
      </button>
    </>
  )
}
